import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { getEvents, getBudgets, createExpense, getCategories, createCategory } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, HelpCircle, FileText, Upload, Send, Plus } from 'lucide-react';
import AIExpenseInput from '@/components/AIExpenseInput';
import FileUploadCard from '@/components/FileUploadCard';

const LogExpense = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [aiParsedData, setAiParsedData] = useState(null);
  const [formData, setFormData] = useState({
    event_id: '',
    budget_id: '',
    amount: '',
    currency: 'INR',
    category: '',
    vendor: '',
    description: '',
  });

  useEffect(() => {
    loadData();
    const hasSeenTutorial = localStorage.getItem('hasSeenExpenseTutorial');
    if (!hasSeenTutorial) {
      setShowTutorial(true);
      localStorage.setItem('hasSeenExpenseTutorial', 'true');
    }
  }, []);

  const loadData = async () => {
    try {
      const [eventsRes, budgetsRes, categoriesRes] = await Promise.all([
        getEvents(),
        getBudgets({ status: 'approved' }),
        getCategories()
      ]);
      const activeEvents = eventsRes.data.filter(e => e.status === 'active');
      setEvents(activeEvents);
      setBudgets(budgetsRes.data);
      setCategories(categoriesRes.data.categories || []);
      if (activeEvents.length > 0 && !formData.event_id) {
        setFormData(prev => ({ ...prev, event_id: activeEvents[0].event_id }));
      }
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const res = await createCategory({ name: newCategoryName.trim() });
      toast.success('Category created!');
      
      // Refresh categories
      const categoriesRes = await getCategories();
      setCategories(categoriesRes.data.categories || []);
      
      // Select the new category
      setFormData(prev => ({ ...prev, category: newCategoryName.trim() }));
      setNewCategoryName('');
      setShowAddCategory(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleAIParsed = (parsed) => {
    setAiParsedData(parsed);
  };

  const handleSubmitAIParsed = async () => {
    if (!formData.event_id) {
      toast.error('Please select an event first');
      return;
    }
    if (!aiParsedData?.amount || !aiParsedData?.description) {
      toast.error('Please provide expense details');
      return;
    }

    setLoading(true);
    try {
      await createExpense({
        event_id: formData.event_id,
        amount: parseFloat(aiParsedData.amount),
        currency: aiParsedData.currency || 'INR',
        category: aiParsedData.category || '',
        vendor: aiParsedData.vendor || '',
        description: aiParsedData.description,
      });
      toast.success('Expense logged successfully!');
      setAiParsedData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log expense');
    } finally {
      setLoading(false);
    }
  };

  const handleFileExpensesParsed = (expenses) => {
    if (expenses.length > 0) {
      const first = expenses[0];
      setFormData({
        ...formData,
        amount: first.amount?.toString() || '',
        category: first.category || '',
        vendor: first.vendor || '',
        description: first.description || '',
        currency: first.currency || 'INR',
      });
      toast.success(`Loaded expense from file. ${expenses.length > 1 ? `${expenses.length - 1} more found.` : ''}`);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!formData.event_id || !formData.amount || !formData.description) {
      toast.error('Please fill event, amount and description');
      return;
    }

    setLoading(true);
    try {
      await createExpense({
        ...formData,
        amount: parseFloat(formData.amount),
      });
      toast.success('Expense logged successfully!');
      setFormData({
        event_id: formData.event_id,
        budget_id: '',
        amount: '',
        currency: 'INR',
        category: '',
        vendor: '',
        description: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/expenses')} size="sm">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Log Expense</h2>
                <p className="text-sm text-muted-foreground">Record your expenses</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowTutorial(true)}>
              <HelpCircle className="h-4 w-4 mr-2" />
              Help
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Event Selector - Always visible */}
            <Card>
              <CardContent className="p-4">
                <Label>Select Event *</Label>
                <Select value={formData.event_id} onValueChange={(value) => setFormData({ ...formData, event_id: value })}>
                  <SelectTrigger className="mt-2" data-testid="event-select">
                    <SelectValue placeholder="Choose event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.event_id} value={event.event_id}>
                        {event.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* SECTION 1: Manual Entry - ON TOP */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="text-lg">Manual Entry</CardTitle>
                    <CardDescription>Fill in the expense details manually</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleManualSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Amount *</Label>
                      <Input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        data-testid="manual-amount"
                      />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                        <SelectTrigger data-testid="manual-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">₹ INR</SelectItem>
                          <SelectItem value="USD">$ USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <div className="flex gap-2">
                      <Select value={formData.category} onValueChange={(value) => {
                        if (value === '__add_new__') {
                          setShowAddCategory(true);
                        } else {
                          setFormData({ ...formData, category: value });
                        }
                      }}>
                        <SelectTrigger data-testid="manual-category" className="flex-1">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.category_id} value={cat.name}>
                              {cat.name}
                              {!cat.is_default && <span className="ml-2 text-xs text-muted-foreground">(custom)</span>}
                            </SelectItem>
                          ))}
                          <SelectItem value="__add_new__" className="text-primary font-medium">
                            <span className="flex items-center gap-1">
                              <Plus className="h-3 w-3" /> Add New Category
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Vendor/Paid To</Label>
                    <Input
                      value={formData.vendor}
                      onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                      placeholder="Who was paid?"
                      data-testid="manual-vendor"
                    />
                  </div>
                  <div>
                    <Label>Description *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="What was this expense for?"
                      rows={2}
                      data-testid="manual-description"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full" data-testid="manual-submit">
                    {loading ? 'Logging...' : 'Log Expense'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* SECTION 2: Upload Receipt */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Upload className="h-5 w-5 text-blue-600" />
                  <div>
                    <CardTitle className="text-lg">Upload Receipt</CardTitle>
                    <CardDescription>Upload an image, PDF, or Excel file</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <FileUploadCard onExpensesParsed={handleFileExpensesParsed} eventId={formData.event_id} compact />
              </CardContent>
            </Card>

            {/* SECTION 3: AI Capture */}
            <Card className="border-primary/30">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/10">
                <div className="flex items-center space-x-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="text-lg">AI Capture</CardTitle>
                    <CardDescription>Type or speak naturally - AI extracts the details</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <AIExpenseInput onParsed={handleAIParsed} eventId={formData.event_id} />
                
                {/* AI Parsed Preview with Submit Button */}
                {aiParsedData && (aiParsedData.amount || aiParsedData.description) && (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-3 flex items-center">
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI Extracted:
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div>
                        <span className="text-muted-foreground">Amount:</span>
                        <p className="font-bold text-lg">{aiParsedData.currency === 'USD' ? '$' : '₹'}{aiParsedData.amount || '0'}</p>
                      </div>
                      {aiParsedData.category && (
                        <div>
                          <span className="text-muted-foreground">Category:</span>
                          <p className="font-semibold">{aiParsedData.category}</p>
                        </div>
                      )}
                      {aiParsedData.vendor && (
                        <div>
                          <span className="text-muted-foreground">Vendor:</span>
                          <p className="font-semibold">{aiParsedData.vendor}</p>
                        </div>
                      )}
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Description:</span>
                        <p className="font-semibold">{aiParsedData.description}</p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleSubmitAIParsed} 
                      disabled={loading}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="ai-submit-button"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {loading ? 'Logging...' : 'Submit This Expense'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>

        <MobileNav user={user} />
      </div>

      {/* Tutorial Dialog */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center">
              <HelpCircle className="h-5 w-5 mr-2 text-primary" />
              How to Log Expenses
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <h4 className="font-semibold mb-1">1. Manual Entry</h4>
              <p className="text-sm text-muted-foreground">Fill in the form directly with amount, category, and description.</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-1">2. Upload Receipt</h4>
              <p className="text-sm text-muted-foreground">Upload a photo of receipt, PDF, or Excel file - AI will extract details.</p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <h4 className="font-semibold mb-1">3. AI Capture</h4>
              <p className="text-sm text-muted-foreground">Just type or speak: "Flight ticket Indigo 5000 rupees" - AI understands!</p>
            </div>
          </div>
          <Button onClick={() => setShowTutorial(false)} className="w-full">
            Got it!
          </Button>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add New Category
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Category Name</Label>
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g., Mobilization Event"
              className="mt-2"
              data-testid="new-category-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              This category will be available for all events.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCategory(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory} data-testid="create-category-btn">
              Create Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogExpense;
